<template>
    <div class="card">
        <div class="card-body">
            <draggable
                :list="items"
                handle=".handle"
                @start="onMoveStart"
                @end="onMoved"
            >
                <div
                    v-for="(question, index) in items"
                    :key="index"
                    class="my-2 row"
                >
                    <div
                        class="col-1 d-flex justify-content-center align-items-center"
                    >
                        <button
                            v-if="editedIndex === null"
                            class="handle btn btn-info"
                            :disabled="!question.id"
                        >
                            <i class="fas fa-arrows-alt" />
                        </button>
                    </div>

                    <div class="col-10 card">
                        <button
                            class="btn"
                            :class="getButtonClass(question)"
                            style="position: absolute; left: 0; width: 100%"
                            @click="editQuestion(index)"
                        >
                            {{ getLabel(question) }}
                        </button>

                        <question-form
                            v-if="index === editedIndex"
                            v-model="items[index]"
                            :validation="$v.items.$each[index]"
                            :server-errors="serverErrors"
                            class="row card-body"
                            style="margin-top: 30px"
                            @save="saveQuestion"
                        />
                    </div>
                    <div
                        class="col-1 d-flex justify-content-center align-items-center"
                    >
                        <button
                            class="btn btn-danger"
                            @click="deleteQuestion(index)"
                        >
                            <i class="far fa-trash-alt" />
                        </button>
                    </div>
                </div>
            </draggable>
            <div class="d-flex justify-content-center" style="margin-top: 30px">
                <button class="btn btn-success" @click="addQuestion">
                    Add new question
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import Draggable from 'vuedraggable';
import { required } from 'vuelidate/lib/validators';
import { StatusCodes as HTTP } from 'http-status-codes';

import QuestionForm from './QuestionForm';
import { questionTypes } from '@/enums/questionTypes';

export default {
    components: {
        Draggable,
        QuestionForm
    },

    props: {
        formId: {
            type: String,
            required: true
        },
        questions: {
            type: Array,
            required: true
        }
    },

    validations: {
        items: {
            $each: {
                label: {
                    required
                },
                width: {
                    required,
                    percentage: v => v >= 0 && v <= 100
                }
            }
        }
    },

    data() {
        return {
            items: [],
            editedIndex: null,
            stringifiedQustionValue: '',
            serverErrors: [],
            drag: false,
            types: questionTypes
        };
    },

    watch: {
        editedIndex(val) {
            if (val !== null) {
                this.stringifiedQustionValue = JSON.stringify(this.items[val]);

                return;
            }

            this.stringifiedQustionValue = '';
        }
    },

    created() {
        this.items = [...this.questions];
    },

    methods: {
        ...mapActions({
            storeQuestion: 'questions/store',
            updateQuestion: 'questions/update',
            destroyQuestion: 'questions/destroy',
            changeQuestionsOrder: 'forms/changeQuestionsOrder'
        }),

        getLabel(question) {
            const { text } = this.types.find(
                ({ value }) => value === question.type
            );

            return `[${text}] ${question.label || '???'}${
                question.isRequired ? '*' : ''
            }`;
        },

        async addQuestion() {
            try {
                await this.saveQuestion(false);
            } catch {
                return;
            }

            this.editedIndex = this.items.length;
            this.items.push({
                type: this.types[0].value,
                width: 100,
                isRequired: false,
                settings: {}
            });
        },

        async editQuestion(index) {
            try {
                await this.saveQuestion(false);
            } catch {
                return;
            }

            this.editedIndex = this.editedIndex === index ? null : index;
        },

        async saveQuestion(forced = true) {
            if (this.editedIndex === null) {
                return;
            }

            this.serverErrors = [];
            const currentQuestion = this.items[this.editedIndex];

            if (
                currentQuestion.id &&
                !forced &&
                this.stringifiedQustionValue === JSON.stringify(currentQuestion)
            ) {
                return;
            }

            try {
                this.validateCurrentQuestion();
            } catch {
                throw new Error('Validation failed');
            }

            const question = {
                ...this.items[this.editedIndex],
                formId: this.formId
            };

            try {
                this.$toaster('Saving...');

                if (!question.id) {
                    const added = await this.storeQuestion(question);

                    this.items.splice(this.editedIndex, 1, added);
                    this.stringifiedQustionValue = JSON.stringify(added);
                } else {
                    const updated = await this.updateQuestion({
                        id: question.id,
                        data: question
                    });

                    this.items.splice(this.editedIndex, 1, updated);
                    this.stringifiedQustionValue = JSON.stringify(updated);
                }

                this.$toaster('Question saved!');
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        throw error;
                    }
                }

                console.error(error);

                this.$toasterError();

                throw error;
            }
        },

        validateCurrentQuestion() {
            const itemValidaton = this.$v.items.$each[this.editedIndex];
            itemValidaton.$touch();

            if (itemValidaton.$invalid) {
                throw new Error('Validation failed.');
            }
        },

        async deleteQuestion(index) {
            try {
                const question = this.items[index];

                if (question.id) {
                    await this.destroyQuestion(question.id);
                }

                this.items.splice(index, 1);

                if (this.editedIndex === index) {
                    this.editedIndex = null;
                }

                this.$toaster('Question removed!');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        async onMoved() {
            this.drag = false;

            const items = this.items.map((item, index) => ({
                id: item.id,
                order: index + 1
            }));

            try {
                await this.changeQuestionsOrder({ id: this.formId, items });

                this.$toaster('Order changed!');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        onMoveStart() {
            this.editedIndex = null;
            this.drag = true;
        },

        getButtonClass({ type }) {
            const { btnVariant } = this.types.find(
                ({ value }) => value === type
            );

            return `btn-${btnVariant}`;
        }
    }
};
</script>
